// Bookmark / wishlist (GoodMood-style). Saved product handles live in localStorage, per browser.
// ponytail: no accounts sync; add customer metafields + app proxy if cross-device lists are ever needed.
const KEY = 'pl-wishlist';
const DATA = 'pl-wishlist-data'; // handle -> {id, title, url, image, price, compare}, so the page draws instantly
const LOG = ''; // Posterly: FGOS logging off
const OUTLINE = 'M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2m0 15-5-2.18L7 18V5h10z';
const FILLED = 'M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2';

const read = (k, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(k)) || fallback;
  } catch {
    return fallback;
  }
};
const write = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {}
};

export const handles = () => read(KEY, []);
export const details = () => read(DATA, {});
export const imageUrl = (src) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=600` : '');

export function remember(handle, d) {
  write(DATA, { ...details(), [handle]: { ...details()[handle], ...d } });
}

// A ~2KB blurry copy kept in localStorage, so the wishlist page never shows an empty square while the
// real photo downloads (the browser cache can't be trusted to still hold it days later).
export function saveThumb(handle, src) {
  if (!src || details()[handle]?.thumb) return;
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const c = document.createElement('canvas');
    c.width = 40;
    c.height = Math.round((40 * img.naturalHeight) / img.naturalWidth) || 40;
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    try {
      remember(handle, { thumb: c.toDataURL('image/webp', 0.6) });
    } catch {} // tainted canvas: skip, the page just waits for the photo
  };
  img.src = `${src}${src.includes('?') ? '&' : '?'}width=40`;
}

function paintCount() {
  const n = handles().length;
  document.querySelectorAll('.wl-count').forEach((b) => {
    b.querySelector('.cart-bubble__text-count').textContent = n > 99 ? '99+' : n;
    b.hidden = !n;
  });
}

// One random id per browser, so FGOS can count distinct shoppers. Nothing personal.
function visitor() {
  let id = localStorage.getItem('pl-visitor');
  if (!id) {
    id = crypto.randomUUID();
    try {
      localStorage.setItem('pl-visitor', id);
    } catch {}
  }
  return id;
}

// Hidden cart attribute "productId:handle,…" rides along to the order, where FGOS's order webhook
// counts lines bought after bookmarking.
function tagCart() {
  const d = details();
  const value = handles()
    .filter((h) => d[h]?.id)
    .map((h) => `${d[h].id}:${h}`)
    .join(',');
  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { _bookmarked: value } }),
  }).catch(() => {});
}

function toggle(button) {
  const { handle, ...d } = button.dataset;
  const list = handles().filter((h) => h !== handle);
  const saved = list.length === handles().length;
  if (saved) {
    list.unshift(handle);
    if (d.id) remember(handle, d);
    if (d.image) {
      new Image().src = imageUrl(d.image); // warm the cache for the wishlist page
      saveThumb(handle, d.image);
    }
  }
  write(KEY, list);
  tagCart();
  pushAccount();
  if (LOG) fetch(LOG, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify({ session: visitor(), event: saved ? 'bookmark' : 'unbookmark', handles: [handle] }),
  }).catch(() => {});
  document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handle, saved } }));
}

document.addEventListener('wishlist:change', paintCount);

document.head.insertAdjacentHTML(
  'beforeend',
  `<style>
    wishlist-button { position: absolute; top: 12px; right: 12px; z-index: 3; }
    wishlist-button button { display: flex; align-items: center; justify-content: center; width: 28px; height: 28px;
      padding: 0; border: 0; border-radius: 50%; background: #fafafa; color: rgb(0 0 0 / .26); cursor: pointer; }
    wishlist-button button[aria-pressed='true'] { color: #070707; }
    wishlist-button button:hover { color: #070707; }
    wishlist-button svg { width: 16px; height: 16px; fill: currentColor; }
    media-gallery wishlist-button { top: 14px; right: 14px; }
    media-gallery wishlist-button button { width: 38px; height: 38px; background: #fff; color: rgb(0 0 0 / .6);
      box-shadow: 0 1px 4px rgb(0 0 0 / .15); }
    media-gallery wishlist-button svg { width: 21px; height: 21px; }
  </style>`
);

class WishlistButton extends HTMLElement {
  connectedCallback() {
    if (!this.firstElementChild) {
      this.innerHTML = `<button type="button" aria-label="Add to wishlist"><svg viewBox="0 0 24 24" aria-hidden="true"><path/></svg></button>`;
      this.firstElementChild.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle(this);
      });
    }
    this.sync();
    document.addEventListener('wishlist:change', this.sync);
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:change', this.sync);
  }

  sync = () => {
    const saved = handles().includes(this.dataset.handle);
    const button = this.firstElementChild;
    button.setAttribute('aria-pressed', saved);
    button.querySelector('path').setAttribute('d', saved ? FILLED : OUTLINE);
  };
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);

// Other tabs
window.addEventListener('storage', (e) => {
  if (e.key === KEY) document.dispatchEvent(new CustomEvent('wishlist:change'));
});

// Logged-in customers: the list also lives on their account (posterly.co/apps/wishlist, a Shopify
// app proxy to old backend, cut off here), so it follows them to other devices.
const PROXY = ''; // Posterly: no wishlist app proxy
const OWNER = 'pl-wishlist-owner'; // which customer this browser's list was last merged with

function pushAccount() {
  if (!window.fgLoggedIn || !PROXY) return;
  const d = details();
  const data = Object.fromEntries(handles().map((h) => [h, { ...d[h], thumb: undefined }]));
  fetch(PROXY, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ handles: handles(), data }),
  }).catch(() => {});
}

async function pullAccount() {
  if (!window.fgLoggedIn || !PROXY) return;
  const r = await fetch(PROXY, { headers: { Accept: 'application/json' } }).catch(() => null);
  const acct = r && r.ok ? await r.json().catch(() => null) : null;
  if (!acct?.customer) return;
  const local = handles();
  const firstTime = localStorage.getItem(OWNER) !== acct.customer;
  // First time on this device: keep both lists. After that the account is the truth, so a print
  // removed on the phone also disappears here.
  const list = firstTime ? [...new Set([...local, ...acct.handles])] : acct.handles;
  for (const [h, d] of Object.entries(acct.data || {})) if (!details()[h]?.id) remember(h, d);
  try {
    localStorage.setItem(OWNER, acct.customer);
  } catch {}
  const changed = list.join() !== local.join();
  if (changed) {
    write(KEY, list);
    document.dispatchEvent(new CustomEvent('wishlist:change'));
  }
  if (firstTime && list.join() !== acct.handles.join()) pushAccount();
  return changed;
}

export const synced = pullAccount();

// A cart made after the last bookmark (e.g. after a checkout) starts without the attribute.
if (handles().length) tagCart();
